import { Fragment, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useHostingSitesV2 } from '@newfold/huapi-js';
import { HostingSitesV2200ItemsItem } from '@newfold/huapi-js/src/index.schemas';

import CommonTooltip from '~/components/CommonTooltip';
import useAccount from '~/hooks/useAccount';
import BtnGroup from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/BtnGroup';
import CardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';

import { defaultFormData, FormDataType } from '../..';

export type ChooseSitePropOptions = {
  formData: FormDataType;
  changeLoading?: boolean;
};

const ChooseSite = ({
  formData,
  changeLoading = false,
}: ChooseSitePropOptions) => {
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const { t } = useTranslation(['addSites', 'common']);

  const { data: hostingSites, isPending } = useHostingSitesV2(
    hostingId,
    {},
    {
      query: {
        select: ({ data }) => data,
        enabled: !!hostingId,
      },
    },
  );

  const siteAddSelectionList =
    hostingSites &&
    hostingSites?.items &&
    hostingSites?.items.filter((item) => item.status === 'active');

  const isAllMultisite = siteAddSelectionList?.every(
    (site) => site?.wp_multi_site?.enable,
  );

  const firstNonMultisite = siteAddSelectionList?.find(
    (site) => !site?.wp_multi_site?.enable,
  );

  const [optionSelected, setOptionSelected] = useState(firstNonMultisite?.id);

  const { setValue, reset } = useFormContext();

  useEffect(() => {
    if (formData?.clone_site_id) {
      setValue('title', formData?.title);
      setValue('clone_site_id', formData?.clone_site_id);
      setOptionSelected(Number(formData?.clone_site_id));
    } else {
      setValue('title', firstNonMultisite?.name!);
      setValue('clone_site_id', firstNonMultisite?.id!);
    }
  }, [setValue, firstNonMultisite, formData]);

  const onSiteSelection = (optionBtn: HostingSitesV2200ItemsItem) => {
    setOptionSelected(optionBtn?.id);
    reset(defaultFormData);
    setValue('title', optionBtn?.name);
    setValue('clone_site_id', optionBtn?.id);
  };

  const handleContinueBtn = () => {
    navigate('../title');
  };

  const handleCancel = () => {
    reset(defaultFormData);
    navigate(-1);
  };
  const isDisableContinueBtn = !optionSelected && isAllMultisite;
  return (
    <Stack spacing={1}>
      <CardHeader
        headerTitle={t('addSites:addSiteOptions.siteClone')}
        hasBackArrow={false}
      />
      <Stack spacing={4}>
        <Grid container>
          <Grid item xs={12} md={10}>
            <Stack spacing={2}>
              {!isPending &&
                siteAddSelectionList?.map((optionBtn, id) => {
                  const isMultisite = optionBtn?.wp_multi_site?.enable;
                  return (
                    <Fragment key={id}>
                      <CommonTooltip
                        title={
                          isMultisite
                            ? t(
                                'addSites:addSiteOptions.multisiteCloneNotSupported',
                              )
                            : ''
                        }
                        placement="top"
                        arrow
                      >
                        <span>
                          <Card
                            key={optionBtn.id}
                            variant={
                              optionBtn?.id === optionSelected && !isMultisite
                                ? 'selected'
                                : 'contained'
                            }
                            onClick={() => {
                              if (isMultisite) return;
                              onSiteSelection(optionBtn);
                            }}
                            data-testid={`clone-site-${optionBtn?.id}`}
                            sx={{
                              borderRadius: '8px',
                              maxWidth: '500px',
                              cursor: isMultisite ? 'not-allowed' : 'pointer',
                              opacity: isMultisite ? 0.5 : 1,
                              pointerEvents: isMultisite ? 'none' : undefined,
                            }}
                          >
                            <CardActionArea>
                              <CardContent>
                                <Stack direction="row" spacing={1}>
                                  <Radio
                                    checked={
                                      optionBtn?.id === optionSelected &&
                                      !isMultisite
                                        ? true
                                        : false
                                    }
                                    disabled={isMultisite}
                                  />
                                  <Stack direction="column" spacing={2}>
                                    <Typography variant="h3">
                                      {optionBtn?.name}
                                    </Typography>
                                    {optionBtn?.domain}
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </span>
                      </CommonTooltip>
                    </Fragment>
                  );
                })}
            </Stack>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <BtnGroup
            onCancelHandler={() => handleCancel()}
            hasSkipBtn={false}
            hasCancelBtn
            hasFinishBtn={false}
            loading={changeLoading}
            onClickHandler={handleContinueBtn}
            disabled={isDisableContinueBtn}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ChooseSite;
